import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Reveal from "../Reveal";
import SectionHeading from "../SectionHeading";
import { PIPELINE_STAGES } from "../../data/content";
import { Database, Cog, BarChart3, BrainCircuit, Lightbulb, Target } from "lucide-react";

const ICONS = [Database, Cog, BarChart3, BrainCircuit, Lightbulb, Target];

function Stage({ stage, i, progress }) {
  const IconCmp = ICONS[i];
  const start = i / PIPELINE_STAGES.length;
  const end = (i + 1) / PIPELINE_STAGES.length;
  const opacity = useTransform(progress, [start - 0.12, start, end, end + 0.2], [0.35, 1, 1, 0.35]);
  const scale = useTransform(progress, [start - 0.12, start, end + 0.2], [0.96, 1, 0.96]);

  return (
    <motion.div style={{ opacity, scale }} className="flex flex-col items-center text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-[#111827]/10 bg-white shadow-[0_12px_30px_-16px_rgba(17,24,39,0.4)]">
        <IconCmp className="h-7 w-7 text-[#1677FF]" />
      </div>
      <p className="mt-4 text-sm font-bold text-[#111827]">{stage.label}</p>
      <p className="mt-1 text-xs leading-snug text-[#6B7280]">{stage.desc}</p>
    </motion.div>
  );
}

export default function DataJourney() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.8", "end 0.4"] });
  const lineWidth = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section className="ad-section bg-[#F7F8FA]" data-testid="data-journey">
      <div className="ad-container">
        <SectionHeading
          eyebrow="The Journey"
          title={<>From raw data to <span className="text-gradient">business impact.</span></>}
          sub="We connect your data ecosystem from ingestion to insight — a scalable foundation for analytics, AI and intelligent automation."
        />

        <div ref={ref} className="mt-16 rounded-3xl border border-[#111827]/8 bg-white p-8 lg:p-14">
          {/* Desktop */}
          <div className="relative hidden lg:block">
            <div className="absolute left-0 right-0 top-8 h-1 rounded bg-[#111827]/8" />
            <motion.div style={{ width: lineWidth }} className="absolute left-0 top-8 h-1 rounded bg-gradient-to-r from-[#1677FF] to-[#00C7D9]" />
            <div className="relative grid grid-cols-6 gap-4">
              {PIPELINE_STAGES.map((s, i) => (
                <Stage key={s.key} stage={s} i={i} progress={scrollYProgress} />
              ))}
            </div>
          </div>

          {/* Mobile */}
          <div className="relative lg:hidden">
            <div className="absolute bottom-6 left-[31px] top-6 w-1 rounded bg-[#111827]/8" />
            <motion.div style={{ height: lineHeight }} className="absolute left-[31px] top-6 w-1 rounded bg-gradient-to-b from-[#1677FF] to-[#00C7D9]" />
            <div className="space-y-6">
              {PIPELINE_STAGES.map((s, i) => {
                const IconCmp = ICONS[i];
                return (
                  <div key={s.key} className="relative flex items-center gap-4">
                    <div className="relative z-10 flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl border border-[#111827]/10 bg-white shadow-sm">
                      <IconCmp className="h-6 w-6 text-[#1677FF]" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-[#111827]">{s.label}</p>
                      <p className="mt-0.5 text-xs text-[#6B7280]">{s.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
